import { useState, useMemo, useRef, useCallback, useEffect, memo } from "react";
import { useLokiQuery, TIME_RANGES } from "./hooks/useLokiStream";

const AUTO_REFRESH_OPTIONS = [
  { label: "Auto refresh off", value: 0       },
  { label: "5s",  value: 5_000   },
  { label: "10s", value: 10_000  },
  { label: "30s", value: 30_000  },
  { label: "1m",  value: 60_000  },
  { label: "5m",  value: 300_000 },
];

import { useTheme } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import WrapTextIcon from "@mui/icons-material/WrapText";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import FilterListIcon from "@mui/icons-material/FilterList";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";

// ─── Level colors — warn included ─────────────────────────────────────────────
const LEVEL_COLORS = {
  trace: "#8b8fa8",
  debug: "#17c1e8",
  info:  "#4caf50",
  warn:  "#fb8c00",
  error: "#f44336",
  fatal: "#9c27b0",
};

const LEVEL_ORDER = ["trace", "debug", "info", "warn", "error", "fatal"];

const stripDunder = (name) => name.replace(/^__(.+)__$/, "$1");

// ─── Native virtual scroll hook ───────────────────────────────────────────────
// Renders only visible rows + overscan — no library needed
function useVirtualScroll({ itemCount, itemHeight, overscan = 20, containerRef }) {
  const [scrollTop, setScrollTop] = useState(0);
  const [height,    setHeight]    = useState(600);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setHeight(el.clientHeight);
    const onScroll = () => setScrollTop(el.scrollTop);
    const onResize = () => setHeight(el.clientHeight);
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(onResize);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", onScroll); ro.disconnect(); };
  }, [containerRef]);

  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const endIndex   = Math.min(itemCount - 1, Math.ceil((scrollTop + height) / itemHeight) + overscan);
  const totalHeight = itemCount * itemHeight;
  const offsetY     = startIndex * itemHeight;

  return { startIndex, endIndex, totalHeight, offsetY };
}

function FieldBar({ pct, color }) {
  return (
    <div style={{ width: "100%", height: "3px", borderRadius: "2px", backgroundColor: "rgba(128,128,128,0.15)", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${pct}%`, backgroundColor: color || "#17c1e8", borderRadius: "2px", transition: "width 0.4s ease" }} />
    </div>
  );
}

// ─── Log Volume Chart ─────────────────────────────────────────────────────────
const LogVolumeChart = memo(function LogVolumeChart({ logs, timeRange, isDark, c, levelFilters, toggleLevelFilter, onClearLevelFilters }) {
  const [collapsed, setCollapsed] = useState(false);
  const [hoverIdx,  setHoverIdx]  = useState(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const containerRef              = useRef(null);
  const NUM_BUCKETS = 60;

  const BAR_COLORS = { trace: "#8b8fa8", debug: "#5794F2", info: "#73BF69", warn: "#F2CC0C", error: "#F2495C", fatal: "#B877D9" };

  const activeLevels = levelFilters && levelFilters.size > 0 ? new Set([...levelFilters]) : null;

  const fmtDatetime = (ms) => {
    const d = new Date(ms);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}:${String(d.getSeconds()).padStart(2,"0")}`;
  };

  const fmtTime = (ms) => {
    const d = new Date(ms);
    if ((timeRange?.ms ?? 0) >= 7 * 24 * 60 * 60 * 1000)
      return `${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}`;
    return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  };

  const { buckets, yMax, yTicks, presentLevels, levelTotals } = useMemo(() => {
    if (!logs.length) return { buckets: [], yMax: 1, yTicks: [0], presentLevels: [], levelTotals: {} };
    const now   = Date.now();
    const start = now - (timeRange?.ms ?? 86_400_000);
    const step  = (now - start) / NUM_BUCKETS;
    const buckets     = Array.from({ length: NUM_BUCKETS }, (_, i) => ({ t: start + i * step, counts: {}, total: 0 }));
    const levelTotals = {};
    const presentSet  = new Set();
    logs.forEach((log) => {
      const idx = Math.min(Math.max(Math.floor(((log.tsMs ?? Date.now()) - start) / step), 0), NUM_BUCKETS - 1);
      const lvl = (log.level || "info").toLowerCase();
      buckets[idx].counts[lvl] = (buckets[idx].counts[lvl] || 0) + 1;
      buckets[idx].total       = (buckets[idx].total || 0) + 1;
      presentSet.add(lvl);
      levelTotals[lvl] = (levelTotals[lvl] || 0) + 1;
    });
    const presentLevels = LEVEL_ORDER.filter((l) => presentSet.has(l));
    let rawMax = 1;
    buckets.forEach((b) => {
      const vis = LEVEL_ORDER.filter((lvl) => b.counts[lvl] > 0 && (!activeLevels || activeLevels.has(lvl))).reduce((s, lvl) => s + b.counts[lvl], 0);
      if (vis > rawMax) rawMax = vis;
    });
    const niceSteps = [1,2,5,10,20,25,50,100,200,250,500,1000,2000,5000];
    const tickStep  = niceSteps.find((s) => s >= rawMax / 4) || 5000;
    const ticks     = [];
    for (let v = 0; v <= rawMax + tickStep; v += tickStep) ticks.push(v);
    const trimmed = ticks.length > 5
      ? [ticks[0], ...ticks.slice(1).filter((_, i, a) => i === a.length - 1 || i % Math.ceil(ticks.length / 4) === 0)]
      : ticks;
    return { buckets, yMax: trimmed[trimmed.length - 1], yTicks: trimmed, presentLevels, levelTotals };
  }, [logs, timeRange, levelFilters]);

  const xTicks = useMemo(() => {
    const every = Math.max(1, Math.floor(NUM_BUCKETS / 10));
    return Array.from({ length: NUM_BUCKETS }, (_, i) => i).filter((i) => i % every === 0);
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const idx  = Math.min(Math.max(Math.floor(((e.clientX - rect.left) / rect.width) * NUM_BUCKETS), 0), NUM_BUCKETS - 1);
    setHoverIdx(idx);
    setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  const CHART_H = 100; const X_AXIS_H = 18; const Y_AXIS_W = 36; const PAD_T = 4; const PAD_R = 8;
  const TT_W = 165; const TT_H = 80;
  const hoverBucket = hoverIdx !== null ? buckets[hoverIdx] : null;

  return (
    <div style={{ borderBottom: `1px solid ${c.border}`, flexShrink: 0, backgroundColor: isDark ? c.bg : "#f8fafc" }}>
      <div onClick={() => setCollapsed((p) => !p)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 12px 4px", cursor: "pointer", userSelect: "none" }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.rowHover; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}>
        <KeyboardArrowDownIcon style={{ fontSize: "14px", color: c.muted, transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.15s" }} />
        <span style={{ fontSize: "12px", color: c.muted, fontWeight: 600, fontFamily: "sans-serif" }}>
          Log volume ({logs.length >= 1000 ? `${(logs.length / 1000).toFixed(1)}K` : logs.length})
        </span>
        {activeLevels && [...activeLevels].map((lvl) => (
          <span key={lvl} style={{ fontSize: "10px", padding: "1px 6px", borderRadius: "8px", backgroundColor: BAR_COLORS[lvl], color: "#fff", fontWeight: 700, textTransform: "uppercase" }}>{lvl}</span>
        ))}
      </div>

      {!collapsed && (
        <>
          <div style={{ display: "flex", paddingRight: `${PAD_R}px` }}>
            <div style={{ width: `${Y_AXIS_W}px`, flexShrink: 0, position: "relative", height: `${CHART_H}px`, marginTop: `${PAD_T}px` }}>
              {yTicks.map((v) => (
                <div key={v} style={{ position: "absolute", right: "6px", top: `${(1 - v / yMax) * (CHART_H - PAD_T)}px`, transform: "translateY(-50%)", fontSize: "10px", color: c.muted, fontFamily: "monospace", whiteSpace: "nowrap", lineHeight: 1 }}>{v}</div>
              ))}
            </div>
            <div ref={containerRef} style={{ flex: 1, position: "relative", height: `${CHART_H + X_AXIS_H}px`, cursor: "crosshair" }} onMouseMove={onMouseMove} onMouseLeave={() => setHoverIdx(null)}>
              <svg viewBox={`0 0 100 ${CHART_H}`} width="100%" height={`${CHART_H}px`} preserveAspectRatio="none" style={{ display: "block", overflow: "visible" }}>
                {yTicks.map((v) => <line key={v} x1="0" y1={(1 - v / yMax) * CHART_H} x2="100" y2={(1 - v / yMax) * CHART_H} stroke={isDark ? c.border : "#e2e8f0"} strokeWidth="0.3" vectorEffect="non-scaling-stroke" />)}
                <line x1="0" y1={CHART_H} x2="100" y2={CHART_H} stroke={isDark ? c.border : "#cbd5e1"} strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
                {buckets.map((bucket, i) => {
                  const visibleLevels = LEVEL_ORDER.filter((lvl) => bucket.counts[lvl] > 0 && (!activeLevels || activeLevels.has(lvl)));
                  if (!visibleLevels.length) return null;
                  const x = (i / NUM_BUCKETS) * 100;
                  const w = (1 / NUM_BUCKETS) * 100;
                  let stackY = CHART_H;
                  return (
                    <g key={i}>
                      {visibleLevels.map((lvl) => {
                        const segH = (bucket.counts[lvl] / yMax) * CHART_H;
                        stackY -= segH;
                        return <rect key={lvl} x={x + 0.075} y={stackY} width={Math.max(w - 0.15, 0.1)} height={segH} fill={BAR_COLORS[lvl]} opacity={hoverIdx === i ? 1 : 0.75} />;
                      })}
                    </g>
                  );
                })}
                {hoverIdx !== null && <line x1={(hoverIdx / NUM_BUCKETS) * 100 + (1 / NUM_BUCKETS) * 50} y1="0" x2={(hoverIdx / NUM_BUCKETS) * 100 + (1 / NUM_BUCKETS) * 50} y2={CHART_H} stroke={isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.15)"} strokeWidth="0.4" vectorEffect="non-scaling-stroke" pointerEvents="none" />}
              </svg>
              <div style={{ position: "relative", height: `${X_AXIS_H}px` }}>
                {xTicks.map((i) => (
                  <div key={i} style={{ position: "absolute", left: `${((i + 0.5) / NUM_BUCKETS) * 100}%`, transform: "translateX(-50%)", fontSize: "10px", color: c.muted, fontFamily: "monospace", whiteSpace: "nowrap", top: "2px" }}>
                    {fmtTime(buckets[i]?.t || 0)}
                  </div>
                ))}
              </div>
              {hoverBucket && hoverIdx !== null && (() => {
                const hasAny = LEVEL_ORDER.some((lvl) => hoverBucket.counts[lvl] > 0);
                if (!hasAny) return null;
                const containerW = containerRef.current?.getBoundingClientRect().width || 800;
                const flipLeft   = cursorPos.x + TT_W + 12 > containerW;
                return (
                  <div style={{ position: "absolute", top: `${Math.max(4, cursorPos.y - TT_H / 2)}px`, left: `${flipLeft ? cursorPos.x - TT_W - 8 : cursorPos.x + 12}px`, backgroundColor: isDark ? "#1a1d2e" : "#ffffff", border: `1px solid ${isDark ? "#2e3250" : "#e2e8f0"}`, borderRadius: "6px", padding: "7px 10px", fontSize: "11px", fontFamily: "monospace", pointerEvents: "none", zIndex: 20, minWidth: `${TT_W}px`, boxShadow: isDark ? "0 4px 16px rgba(0,0,0,0.5)" : "0 2px 12px rgba(0,0,0,0.12)" }}>
                    <div style={{ fontWeight: 700, color: isDark ? "#e2e4f0" : "#1f2937", marginBottom: "6px" }}>{fmtDatetime(hoverBucket.t)}</div>
                    {presentLevels.map((lvl) => {
                      const cnt = hoverBucket.counts[lvl] || 0;
                      if (!cnt) return null;
                      return (
                        <div key={lvl} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", opacity: activeLevels && !activeLevels.has(lvl) ? 0.3 : 1, marginBottom: "2px" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <svg width="14" height="3" viewBox="0 0 14 3"><line x1="0" y1="1.5" x2="14" y2="1.5" stroke={BAR_COLORS[lvl]} strokeWidth="2.5" strokeLinecap="round" /></svg>
                            <span style={{ color: isDark ? "#e2e4f0" : "#374151" }}>{lvl}</span>
                          </span>
                          <span style={{ color: isDark ? "#e2e4f0" : "#374151", fontWeight: 700 }}>{cnt}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "4px 12px 7px", flexWrap: "wrap" }}>
            {presentLevels.map((lvl) => {
              const isFiltered = activeLevels && !activeLevels.has(lvl);
              return (
                <span key={lvl} onClick={() => toggleLevelFilter && toggleLevelFilter(lvl)} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", fontFamily: "sans-serif", cursor: "pointer", userSelect: "none", opacity: isFiltered ? 0.4 : 1, transition: "opacity 0.15s" }}>
                  <svg width="14" height="3" viewBox="0 0 14 3"><line x1="0" y1="1.5" x2="14" y2="1.5" stroke={BAR_COLORS[lvl]} strokeWidth="2.5" strokeLinecap="round" /></svg>
                  <span style={{ color: isFiltered ? c.muted : BAR_COLORS[lvl], fontWeight: 600, textDecoration: isFiltered ? "line-through" : "none" }}>{lvl}</span>
                  <span style={{ color: c.muted }}>Total: {levelTotals[lvl] || 0}</span>
                </span>
              );
            })}
            {activeLevels && <span onClick={() => onClearLevelFilters()} style={{ fontSize: "10px", color: "#f87171", cursor: "pointer" }}>clear</span>}
          </div>
        </>
      )}
    </div>
  );
});

const LabelsTab = memo(function LabelsTab({ logs, onFilterClick, isDark, c }) {
  const labelStats = useMemo(() => {
    const counts = {};
    logs.forEach((log) => Object.entries(log.labels || {}).forEach(([k, v]) => {
      if (!counts[k]) counts[k] = { values: {}, total: 0 };
      const sv = String(v);
      counts[k].values[sv] = (counts[k].values[sv] || 0) + 1;
      counts[k].total++;
    }));
    return Object.entries(counts).map(([name, { values, total }]) => ({ name, values, total, cardinality: Object.keys(values).length })).sort((a, b) => b.total - a.total);
  }, [logs]);
  if (!logs.length)       return <div style={{ padding: "24px", textAlign: "center", color: c.muted, fontSize: "13px" }}>No logs loaded</div>;
  if (!labelStats.length) return <div style={{ padding: "24px", textAlign: "center", color: c.muted, fontSize: "13px" }}>No labels found</div>;
  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
      {labelStats.map(({ name, values, total: _total, cardinality }) => (
        <div key={name} style={{ borderBottom: `1px solid ${c.border}`, padding: "6px 12px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
            <span style={{ fontSize: "12px", fontWeight: 600, color: isDark ? "#a5b4fc" : "#4f46e5", fontFamily: "monospace" }}>{name}</span>
            <span style={{ fontSize: "11px", color: c.muted }}>{cardinality} value{cardinality !== 1 ? "s" : ""}</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {Object.entries(values).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([val, cnt]) => (
              <span key={val} onClick={() => onFilterClick(name, val)} style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "10px", fontFamily: "monospace", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", backgroundColor: isDark ? "#1e2d1e" : "#dcfce7", color: "#4ade80" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? "#253d25" : "#bbf7d0"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? "#1e2d1e" : "#dcfce7"; }}>
                {stripDunder(val)}<span style={{ color: c.muted, fontSize: "9px" }}>{cnt}</span>
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});

const FieldsTab = memo(function FieldsTab({ logs, onFilterClick, isDark, c }) {
  const fieldStats = useMemo(() => {
    const counts = {};
    logs.forEach((log) => Object.entries(log.fields || {}).forEach(([k, v]) => {
      if (!counts[k]) counts[k] = { values: {}, total: 0 };
      const sv = String(v);
      counts[k].values[sv] = (counts[k].values[sv] || 0) + 1;
      counts[k].total++;
    }));
    return Object.entries(counts).map(([name, { values, total }]) => ({ name, values, total, cardinality: Object.keys(values).length })).sort((a, b) => b.total - a.total);
  }, [logs]);
  if (!logs.length)       return <div style={{ padding: "24px", textAlign: "center", color: c.muted, fontSize: "13px" }}>No logs loaded</div>;
  if (!fieldStats.length) return <div style={{ padding: "24px", textAlign: "center", color: c.muted, fontSize: "13px" }}>No fields detected</div>;
  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
      {fieldStats.map(({ name, values, total, cardinality }) => (
        <div key={name} style={{ borderBottom: `1px solid ${c.border}`, padding: "6px 12px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
            <span style={{ fontSize: "12px", fontWeight: 600, color: isDark ? "#93c5fd" : "#2563eb", fontFamily: "monospace" }}>{name}</span>
            <span style={{ fontSize: "11px", color: c.muted }}>{Math.round((total / logs.length) * 100)}% · {cardinality} value{cardinality !== 1 ? "s" : ""}</span>
          </div>
          <FieldBar pct={Math.round((total / logs.length) * 100)} color="#17c1e8" />
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "4px" }}>
            {Object.entries(values).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([val, cnt]) => (
              <span key={val} onClick={() => onFilterClick(name, val)} style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "10px", fontFamily: "monospace", cursor: "pointer", backgroundColor: isDark ? "#2d2344" : "#eee8ff", color: "var(--light-primary-color, #7443ff)" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? "#3b2b63" : "#dcd0ff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? "#2d2344" : "#eee8ff"; }}>
                {String(val).slice(0, 40)}{String(val).length > 40 ? "…" : ""}
                <span style={{ color: c.muted, fontSize: "9px", marginLeft: "3px" }}>{cnt}</span>
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});

const PatternsTab = memo(function PatternsTab({ logs, isDark, c }) {
  const patterns = useMemo(() => {
    if (!logs.length) return [];
    const normalize = (line) =>
      line
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "<uuid>")
        .replace(/\b\d{10,}\b/g, "<ts>").replace(/\b0x[0-9a-f]+\b/gi, "<hex>")
        .replace(/\b\d{4}-\d{2}-\d{2}T[\d:.Z+-]+/g, "<datetime>").replace(/\b\d+\b/g, "<num>");
    const clusters = {};
    logs.forEach((log) => {
      const key = normalize(log.line).slice(0, 120);
      if (!clusters[key]) clusters[key] = { pattern: key, count: 0, sample: log.line, levels: {} };
      clusters[key].count++;
      clusters[key].levels[log.level || "info"] = (clusters[key].levels[log.level || "info"] || 0) + 1;
    });
    return Object.values(clusters).sort((a, b) => b.count - a.count);
  }, [logs]);
  if (!logs.length) return <div style={{ padding: "24px", textAlign: "center", color: c.muted, fontSize: "13px" }}>No logs loaded</div>;
  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
      {patterns.map((p, i) => (
        <div key={i} style={{ borderBottom: `1px solid ${c.border}`, padding: "8px 12px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px", marginBottom: "4px" }}>
            <span style={{ fontSize: "11px", fontFamily: "monospace", color: c.text, wordBreak: "break-all", whiteSpace: "pre-wrap", flex: 1 }}>{p.pattern}</span>
            <span style={{ fontSize: "12px", fontWeight: 700, color: isDark ? "#a5b4fc" : "#4f46e5", flexShrink: 0 }}>{p.count}</span>
          </div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {Object.entries(p.levels).map(([lvl, cnt]) => (
              <span key={lvl} style={{ fontSize: "10px", padding: "1px 6px", borderRadius: "8px", color: LEVEL_COLORS[lvl] || c.muted, border: `1px solid ${LEVEL_COLORS[lvl] || c.border}`, fontWeight: 700, textTransform: "uppercase" }}>{lvl} {cnt}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});

function SectionHeader({ label, collapsed, onToggle, isDark, c }) {
  return (
    <SoftBox px={1.5} pt={1} pb={0.5} display="flex" justifyContent="space-between" alignItems="center" onClick={onToggle} sx={{ cursor: "pointer", "&:hover": { backgroundColor: isDark ? c.rowHover : "#f0f4ff" } }}>
      <SoftTypography sx={{ fontSize: "10px", color: c.muted, letterSpacing: "0.08em", fontWeight: 700 }}>{label}</SoftTypography>
      <KeyboardArrowDownIcon sx={{ fontSize: "14px", color: c.muted, transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.15s" }} />
    </SoftBox>
  );
}

function PanelToggle({ label, icon, active, onClick, isDark, c }) {
  return (
    <div onClick={onClick} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 16px", borderBottom: `1px solid ${c.border}`, cursor: "pointer" }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.rowHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {icon && <span style={{ color: c.muted, display: "flex", alignItems: "center" }}>{icon}</span>}
        <span style={{ fontSize: "13px", color: c.text, fontFamily: "sans-serif" }}>{label}</span>
      </div>
      {active !== undefined && (
        <div style={{ width: "32px", height: "18px", borderRadius: "9px", flexShrink: 0, backgroundColor: active ? "var(--primary-color, #7443ff)" : isDark ? c.border : "#d1d5db", position: "relative", transition: "background-color 0.2s" }}>
          <div style={{ position: "absolute", top: "2px", left: active ? "16px" : "2px", width: "14px", height: "14px", borderRadius: "50%", backgroundColor: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
        </div>
      )}
    </div>
  );
}

function PanelGroupHeader({ label, collapsed, onToggle, isDark, c }) {
  return (
    <div onClick={onToggle} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 16px", borderBottom: `1px solid ${c.border}`, cursor: "pointer", backgroundColor: c.header }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? c.rowHover : "#e8ebf4"; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = c.header; }}>
      <span style={{ fontSize: "11px", color: c.muted, fontWeight: 700, letterSpacing: "0.08em", fontFamily: "sans-serif" }}>{label}</span>
      <KeyboardArrowDownIcon style={{ fontSize: "14px", color: c.muted, transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.15s" }} />
    </div>
  );
}

const IMPORTANT_KEYS  = new Set(["flow_id", "flow_name", "error", "cause", "exception", "message", "msg", "reason", "status", "code"]);
const ERROR_KEY_RE    = /error|exception|fail|cause/i;
const EXCEPTION_RE    = /[A-Z][a-z]+(Exception|Error|Failure|Fault)/;
const UUID_RE         = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KV_SPLIT_RE     = /([\w.-]+=[^\s|,]+[^\s|,]*)/g;
const LEVEL_TAG_RE    = /^\[(TRACE|DEBUG|INFO|WARN|ERROR|FATAL)\s*\]$/i;
const FONT_SIZE_MAP   = { small: "12px", medium: "15px", large: "18px" };
const TS_SIZE_MAP     = { small: "10px", medium: "13px", large: "16px" };
const ROW_H           = 24; // collapsed row height in px — used by virtual scroll

// ─── LogRow ───────────────────────────────────────────────────────────────────
const LogRow = memo(function LogRow({ log, rowKey, isExpanded, onToggle, onFieldClick, selectedFields, displayMs, wrapLines, fontSizeOption, highlightText, isDark, c }) {
  const levelColor = LEVEL_COLORS[log.level] || "transparent";
  const fontSize   = FONT_SIZE_MAP[fontSizeOption]  || "15px";
  const tsSize     = TS_SIZE_MAP[fontSizeOption]     || "13px";

  const renderLine = useCallback((rawLine) => {
    if (!highlightText) return rawLine;
    const tokens  = [];
    let lastIndex = 0;
    let match;
    const re      = new RegExp(KV_SPLIT_RE.source, "g");
    while ((match = re.exec(rawLine)) !== null) {
      if (match.index > lastIndex) tokens.push({ type: "text", value: rawLine.slice(lastIndex, match.index) });
      tokens.push({ type: "kv", value: match[0] });
      lastIndex = re.lastIndex;
    }
    if (lastIndex < rawLine.length) tokens.push({ type: "text", value: rawLine.slice(lastIndex) });
    if (!tokens.length)             tokens.push({ type: "text", value: rawLine });

    return tokens.map((token, i) => {
      if (token.type === "kv") {
        const eqIdx   = token.value.indexOf("=");
        const key     = token.value.slice(0, eqIdx);
        const rawVal  = token.value.slice(eqIdx + 1);
        const value   = rawVal.replace(/[,|]+$/, "");
        const trailer = rawVal.slice(value.length);
        const isError     = ERROR_KEY_RE.test(key);
        const isImportant = IMPORTANT_KEYS.has(key.toLowerCase());
        return (
          <span key={i}>
            <span style={{ color: isError ? (isDark ? "#fca5a5" : "#dc2626") : isImportant ? (isDark ? "#fcd34d" : "#d97706") : isDark ? "#93c5fd" : "#2563eb", fontWeight: isImportant || isError ? 700 : 400 }}>{key}</span>
            <span style={{ color: c.muted }}>=</span>
            <span style={{ color: isError ? (isDark ? "#f87171" : "#ef4444") : isImportant ? (isDark ? "#fde68a" : "#b45309") : isDark ? "#86efac" : "#16a34a", fontWeight: isImportant || isError ? 700 : 400, backgroundColor: isError ? (isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.08)") : isImportant ? (isDark ? "rgba(251,191,36,0.12)" : "rgba(217,119,6,0.08)") : "transparent", borderRadius: "3px", padding: isImportant || isError ? "0 3px" : "0" }}>{value}</span>
            {trailer && <span style={{ color: c.muted }}>{trailer}</span>}
          </span>
        );
      }
      return (
        <span key={i}>
          {token.value.split(/(\s+)/g).map((word, j) => {
            const trimmed = word.trim();
            if (LEVEL_TAG_RE.test(trimmed)) {
              const lvl = trimmed.replace(/[[\]\s]/g, "").toLowerCase();
              return <span key={j} style={{ color: LEVEL_COLORS[lvl] || "#8b8fa8", fontWeight: 700 }}>{word}</span>;
            }
            if (UUID_RE.test(trimmed))   return <span key={j} style={{ color: isDark ? "#fcd34d" : "#d97706", fontWeight: 700 }}>{word}</span>;
            if (EXCEPTION_RE.test(word)) return <span key={j} style={{ color: isDark ? "#fca5a5" : "#dc2626", fontWeight: 700, backgroundColor: isDark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)", borderRadius: "3px", padding: "0 3px" }}>{word}</span>;
            if (trimmed === "|")         return <span key={j} style={{ color: c.muted }}>{word}</span>;
            return word;
          })}
        </span>
      );
    });
  }, [highlightText, isDark, c]);

  const timestamp = displayMs ? new Date(log.rawTime || log.time).toISOString() : log.time;

  return (
    <div style={{ willChange: "transform" }}>
      <div
        onClick={() => onToggle(rowKey)}
        style={{ display: "flex", gap: "8px", alignItems: "flex-start", padding: "2px 8px", borderRadius: "3px", cursor: "pointer", borderLeft: `2px solid ${levelColor}`, marginBottom: "1px", minWidth: "max-content", width: "100%", height: `${ROW_H}px`, boxSizing: "border-box" }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.rowHover; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
      >
        <span style={{ color: c.muted, flexShrink: 0, display: "flex", alignItems: "center", height: "100%" }}>
          <KeyboardArrowDownIcon style={{ fontSize: "14px", transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.15s" }} />
        </span>
        <span style={{ color: c.muted, fontSize: tsSize, minWidth: displayMs ? "195px" : "155px", flexShrink: 0, fontFamily: "monospace", userSelect: "none", lineHeight: `${ROW_H}px` }}>{timestamp}</span>
        <span style={{ color: levelColor, fontSize: tsSize, fontWeight: 700, minWidth: "46px", flexShrink: 0, textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: `${ROW_H}px`, fontFamily: "sans-serif" }}>{log.level || "INFO"}</span>
        {selectedFields.map((fieldKey) => {
          const val = log.fields?.[fieldKey] ?? log.labels?.[fieldKey];
          return (
            <span key={fieldKey} onClick={(e) => { e.stopPropagation(); if (val != null) onFieldClick(fieldKey, String(val)); }}
              style={{ fontSize: tsSize, flexShrink: 0, minWidth: "80px", maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: val != null ? (isDark ? "#a5b4fc" : "#4f46e5") : c.muted, fontFamily: "monospace", lineHeight: `${ROW_H}px`, cursor: val != null ? "pointer" : "default", borderRight: `1px solid ${c.border}`, paddingRight: "8px" }}>
              {val != null ? String(val) : "—"}
            </span>
          );
        })}
        <span style={{ color: c.text, fontSize, fontFamily: "monospace", wordBreak: wrapLines ? "break-all" : "keep-all", whiteSpace: wrapLines ? "pre-wrap" : "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: `${ROW_H}px`, flex: 1, minWidth: 0 }}>
          {renderLine(log.line)}
        </span>
      </div>

      {isExpanded && (
        <div style={{ marginLeft: "28px", marginBottom: "6px", padding: "10px 12px", borderRadius: "6px", backgroundColor: isDark ? "#0d0f18" : "#f8fafc", border: `1px solid ${c.border}`, borderLeft: `3px solid ${levelColor}` }}>
          <div style={{ marginBottom: "8px" }}>
            <div style={{ fontSize: "10px", color: c.muted, letterSpacing: "0.08em", marginBottom: "4px", fontFamily: "sans-serif" }}>LOG LINE</div>
            <div style={{ fontSize: "11px", color: c.text, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{log.line}</div>
          </div>
          {Object.keys(log.fields || {}).length > 0 && (
            <div style={{ marginBottom: "6px" }}>
              <div style={{ fontSize: "10px", color: c.muted, letterSpacing: "0.08em", marginBottom: "4px", fontFamily: "sans-serif" }}>FIELDS</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                {Object.entries(log.fields).map(([k, v]) => (
                  <span key={k} onClick={(e) => { e.stopPropagation(); onFieldClick(k, String(v)); }}
                    style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "10px", fontFamily: "monospace", cursor: "pointer", display: "inline-block", backgroundColor: isDark ? "#2d2344" : "#eee8ff", color: "var(--light-primary-color, #7443ff)" }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? "#3b2b63" : "#dcd0ff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? "#2d2344" : "#eee8ff"; }}>
                    {stripDunder(k)}=<strong style={{ color: isDark ? "#93c5fd" : "#2563eb" }}>{String(v)}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}
          {Object.keys(log.labels || {}).length > 0 && (
            <div>
              <div style={{ fontSize: "10px", color: c.muted, letterSpacing: "0.08em", marginBottom: "4px", fontFamily: "sans-serif" }}>LABELS</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                {Object.entries(log.labels).map(([k, v]) => (
                  <span key={k} onClick={(e) => { e.stopPropagation(); onFieldClick(k, String(v)); }}
                    style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "10px", fontFamily: "monospace", cursor: "pointer", display: "inline-block", backgroundColor: isDark ? "#1e2d1e" : "#dcfce7", color: "#4ade80" }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? "#253d25" : "#bbf7d0"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? "#1e2d1e" : "#dcfce7"; }}>
                    {stripDunder(k)}=<strong style={{ color: isDark ? "#86efac" : "#16a34a" }}>{String(v)}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// ─── Virtualized log list ─────────────────────────────────────────────────────
const VirtualLogList = memo(function VirtualLogList({ logs, expandedRows, onToggle, onFieldClick, selectedFields, displayMs, wrapLines, fontSizeOption, highlightText, isDark, c, scrollRef }) {
  const { startIndex, endIndex, totalHeight, offsetY } = useVirtualScroll({
    itemCount:    logs.length,
    itemHeight:   ROW_H,
    overscan:     30,
    containerRef: scrollRef,
  });

  const visibleLogs = logs.slice(startIndex, endIndex + 1);

  return (
    <div style={{ position: "relative", height: `${totalHeight}px`, width: "100%" }}>
      <div style={{ position: "absolute", top: `${offsetY}px`, left: 0, right: 0 }}>
        {visibleLogs.map((log, i) => {
          const rowKey = log.id || String(startIndex + i);
          return (
            <LogRow
              key={rowKey}
              log={log}
              rowKey={rowKey}
              isExpanded={expandedRows.has(rowKey)}
              onToggle={onToggle}
              onFieldClick={onFieldClick}
              selectedFields={selectedFields}
              displayMs={displayMs}
              wrapLines={wrapLines}
              fontSizeOption={fontSizeOption}
              highlightText={highlightText}
              isDark={isDark}
              c={c}
            />
          );
        })}
      </div>
    </div>
  );
});

function ToolbarDropdown({ options = [], value, onChange, isDark, c, minWidth = 120, accent = false }) {
  const [open, setOpen] = useState(false);
  const rootRef         = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const safeOptions = Array.isArray(options) ? options : [];
  const selected    = safeOptions.find((o) => o.value === value) ?? safeOptions[0] ?? { label: "", value: "" };

  return (
    <div ref={rootRef} style={{ position: "relative", flexShrink: 0 }}>
      <div onClick={() => setOpen((p) => !p)}
        style={{ height: "36px", minWidth: `${minWidth}px`, padding: "0 10px 0 12px", display: "flex", alignItems: "center", gap: "6px", borderRadius: "8px", border: `1px solid ${accent ? "var(--primary-color, #7443ff)" : c.border}`, backgroundColor: c.inputBg, cursor: "pointer", userSelect: "none", boxSizing: "border-box" }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? c.rowHover : "#f8fafc"; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = c.inputBg; }}>
        <span style={{ fontSize: "13px", color: c.text, fontFamily: "sans-serif", flex: 1, whiteSpace: "nowrap" }}>{selected.label}</span>
        <KeyboardArrowDownIcon style={{ fontSize: "16px", color: c.muted, flexShrink: 0, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }} />
      </div>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, minWidth: `${minWidth}px`, backgroundColor: c.inputBg, border: `1px solid ${c.border}`, borderRadius: "8px", boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.5)" : "0 4px 16px rgba(0,0,0,0.12)", zIndex: 999, overflow: "hidden", padding: "4px 0" }}>
          {safeOptions.map((opt) => {
            const isActive = opt.value === value;
            return (
              <div key={String(opt.value)} onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "7px 14px", fontSize: "13px", fontFamily: "sans-serif", color: isActive ? "var(--primary-color, #7443ff)" : c.text, backgroundColor: isActive ? (isDark ? "#2d2344" : "#eee8ff") : "transparent", cursor: "pointer", whiteSpace: "nowrap", fontWeight: isActive ? 600 : 400 }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = c.rowHover; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = "transparent"; }}>
                {opt.icon && <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{opt.icon}</span>}
                <span style={{ flex: 1 }}>{opt.label}</span>
                {isActive && <span style={{ fontSize: "12px", color: "var(--primary-color, #7443ff)" }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════
export default function LokiLogViewer() {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [viewMode,        setViewMode]        = useState("Logs");
  const [subTab,          setSubTab]          = useState("Logs");
  const [timeRange,       setTimeRange]       = useState(TIME_RANGES[5]);
  const [filterText,      setFilterText]      = useState("");
  const [downloadFormat,  setDownloadFormat]  = useState("json");
  const [fieldSearch,     setFieldSearch]     = useState("");
  const [fieldFilters,    setFieldFilters]    = useState([]);
  const [selectedFields,  setSelectedFields]  = useState([]);
  const [expandedRows,    setExpandedRows]    = useState(new Set());
  const [levelFilters,    setLevelFilters]    = useState(new Set());
  const [showLevelFilter, setShowLevelFilter] = useState(false);
  const [labelFilterText, setLabelFilterText] = useState("");
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);

  const [collapsedSections,      setCollapsedSections]      = useState({ selectedFields: false, suggested: false, fields: false });
  const [collapsedPanelSections, setCollapsedPanelSections] = useState({ display: false, filter: false, actions: false });
  const toggleSection      = useCallback((key) => setCollapsedSections((p)      => ({ ...p, [key]: !p[key] })), []);
  const togglePanelSection = useCallback((key) => setCollapsedPanelSections((p) => ({ ...p, [key]: !p[key] })), []);

  const [displayOpts, setDisplayOpts] = useState({
    newestFirst:   true,
    wrapLines:     false,
    displayMs:     true,
    fontSize:      "medium",
    highlightText: true,
    deduplication: false,
    showSearchBar: false,
    showLogLevel:  true,
  });

  const logContainerRef = useRef(null);
  const toggleOpt       = useCallback((key) => setDisplayOpts((p) => ({ ...p, [key]: !p[key] })), []);

  const {
    logs,
    total,
    status,
    errorMsg,
    lastFetch,
    nextRefreshIn,
    refresh,
    clearLogs,
    downloadLogs,
  } = useLokiQuery({ timeRange, autoRefreshInterval, flowId: null });

  // ── Derived stats from ALL logs ───────────────────────────────────────────
  const fieldStats = useMemo(() => {
    if (!logs.length) return [];
    const counts = {};
    logs.forEach((log) => Object.keys(log.fields || {}).forEach((k) => { counts[k] = (counts[k] || 0) + 1; }));
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count, percentage: Math.round((count / logs.length) * 100) }))
      .sort((a, b) => b.count - a.count);
  }, [logs]);

  const detectedLevels = useMemo(() => {
    const levels = new Set();
    logs.forEach((log) => { const lvl = (log.level || "").toLowerCase(); if (lvl) levels.add(lvl); });
    return levels;
  }, [logs]);

  const labelKeyCount = useMemo(() => { const keys = new Set(); logs.forEach((log) => Object.keys(log.labels || {}).forEach((k) => keys.add(k))); return keys.size; }, [logs]);
  const fieldKeyCount = useMemo(() => { const keys = new Set(); logs.forEach((log) => Object.keys(log.fields || {}).forEach((k) => keys.add(k))); return keys.size; }, [logs]);

  const patternCount = useMemo(() => {
    if (!logs.length) return 0;
    const normalize = (line) =>
      line
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "<uuid>")
        .replace(/\b\d{10,}\b/g, "<ts>").replace(/\b0x[0-9a-f]+\b/gi, "<hex>")
        .replace(/\b\d{4}-\d{2}-\d{2}T[\d:.Z+-]+/g, "<datetime>").replace(/\b\d+\b/g, "<num>");
    return new Set(logs.map((l) => normalize(l.line).slice(0, 120))).size;
  }, [logs]);

  const toggleLevelFilter  = useCallback((level) => setLevelFilters((p) => { const n = new Set(p); n.has(level) ? n.delete(level) : n.add(level); return n; }), []);
  const HIDDEN_FIELDS      = useMemo(() => new Set(["tenant_id"]), []);
  const filteredFieldStats = useMemo(() =>
    fieldStats.filter((f) => !HIDDEN_FIELDS.has(f.name) && (!fieldSearch.trim() || f.name.toLowerCase().includes(fieldSearch.toLowerCase()))),
    [fieldStats, fieldSearch, HIDDEN_FIELDS]
  );

  const toggleSelectedField = useCallback((name) => setSelectedFields((p) => p.includes(name) ? p.filter((f) => f !== name) : [...p, name]), []);
  const addFieldFilter      = useCallback((key, value) => setFieldFilters((p) => p.some((f) => f.key === key && f.value === value) ? p : [...p, { key, value }]), []);
  const removeFieldFilter   = useCallback((key, value) => setFieldFilters((p) => p.filter((f) => !(f.key === key && f.value === value))), []);
  const toggleRow           = useCallback((id) => setExpandedRows((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }), []);
  const scrollToTop         = useCallback(() => logContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" }), []);
  const scrollToBottom      = useCallback(() => logContainerRef.current?.scrollTo({ top: logContainerRef.current.scrollHeight, behavior: "smooth" }), []);

  // ── Client-side filters — memoized, applied to ALL logs ───────────────────
  const filteredLogs = useMemo(() => {
    let result = logs;
    if (filterText.trim()) {
      const q = filterText.toLowerCase();
      result = result.filter((l) => l.line.toLowerCase().includes(q));
    }
    if (fieldFilters.length) {
      result = result.filter((l) =>
        fieldFilters.every((f) => {
          const val = l.fields?.[f.key] ?? l.labels?.[f.key];
          if (f.value === "*") return val != null && String(val).trim() !== "";
          return val != null && String(val) === f.value;
        })
      );
    }
    if (levelFilters.size > 0) {
      result = result.filter((l) => levelFilters.has((l.level || "info").toLowerCase()));
    }
    if (displayOpts.deduplication) {
      const seen = new Set();
      result = result.filter((l) => { if (seen.has(l.line)) return false; seen.add(l.line); return true; });
    }
    return [...result].sort((a, b) => {
      const aNs = a.id.split("-")[0];
      const bNs = b.id.split("-")[0];
      if (displayOpts.newestFirst) return bNs > aNs ? 1 : bNs < aNs ? -1 : 0;
      return aNs > bNs ? 1 : aNs < bNs ? -1 : 0;
    });
  }, [logs, filterText, fieldFilters, levelFilters, displayOpts.deduplication, displayOpts.newestFirst]);

  const c = useMemo(() => ({
    bg:        "var(--portal-canvas-bg)",
    header:    "var(--portal-surface-bg)",
    border:    isDark ? "var(--portal-surface-border)" : "#e5e7eb",
    text:      isDark ? "var(--main-font-color)" : "#1f2937",
    muted:     isDark ? "#9da9b6" : "#6b7280",
    inputBg:   isDark ? "var(--main-bg-color)" : "#ffffff",
    rowHover:  isDark ? "#1b2835" : "#f0f2f6",
    sidebarBg: "var(--portal-surface-bg)",
    panelBg:   "var(--portal-surface-bg)",
  }), [isDark]);

  const isLoading = status === "loading";

  const subTabs = useMemo(() => [
    { key: "Logs",     label: "Logs",     count: filteredLogs.length },
    { key: "Labels",   label: "Labels",   count: labelKeyCount },
    { key: "Fields",   label: "Fields",   count: fieldKeyCount },
    { key: "Patterns", label: "Patterns", count: patternCount },
  ], [filteredLogs.length, labelKeyCount, fieldKeyCount, patternCount]);

  return (
    <SoftBox sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0, backgroundColor: c.bg, borderRadius: 0, border: "none", overflow: "hidden", fontFamily: "sans-serif" }}>

      {/* ── Top bar ──────────────────────────────────────────────────────────── */}
      <SoftBox sx={{ borderBottom: `1px solid ${c.border}`, backgroundColor: c.header, flexShrink: 0 }}>

        {/* Row 1: Labels filter */}
        <SoftBox display="flex" alignItems="center" gap={1.5} px={2.5} py={1.5} sx={{ borderBottom: `1px solid ${c.border}`, flexWrap: "wrap" }}>
          <SoftTypography sx={{ fontSize: "12px", color: c.muted, fontWeight: 600, whiteSpace: "nowrap", userSelect: "none" }}>Labels</SoftTypography>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, minWidth: 0, border: `1px solid ${c.border}`, borderRadius: "6px", backgroundColor: c.inputBg, padding: "3px 8px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: isDark ? c.rowHover : "#e8ebf4", borderRadius: "4px", padding: "2px 6px 2px 4px" }}>
              <TuneIcon style={{ fontSize: "12px", color: c.muted }} />
              <span style={{ fontSize: "12px", color: c.text, fontFamily: "monospace", whiteSpace: "nowrap" }}>service_name = cxf-executor</span>
            </div>
            {fieldFilters.filter((f) => f._isLabel).map((f) => (
              <div key={`${f.key}=${f.value}`} style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: isDark ? "#1e2d1e" : "#dcfce7", borderRadius: "4px", padding: "2px 4px 2px 6px" }}>
                <span style={{ fontSize: "12px", color: isDark ? "#86efac" : "#16a34a", fontFamily: "monospace", whiteSpace: "nowrap" }}>{f.key} = {f.value}</span>
                <CloseIcon onClick={() => removeFieldFilter(f.key, f.value)} style={{ fontSize: "12px", color: c.muted, cursor: "pointer" }} />
              </div>
            ))}
            <input value={labelFilterText} onChange={(e) => setLabelFilterText(e.target.value)} placeholder="Filter by labels" style={{ border: "none", outline: "none", background: "transparent", fontSize: "12px", color: c.text, fontFamily: "sans-serif", minWidth: "120px", flex: 1 }} />
            {labelFilterText && <CloseIcon onClick={() => setLabelFilterText("")} style={{ fontSize: "14px", color: c.muted, cursor: "pointer", flexShrink: 0 }} />}
          </div>
          {status === "error" && <Chip label={`Error: ${errorMsg}`} size="small" sx={{ backgroundColor: "#3b1212", color: "#f87171", fontSize: "11px" }} />}
        </SoftBox>

        {/* Row 2: Controls */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={2} px={2.5} py={1.5} sx={{ minHeight: "56px", flexWrap: "wrap" }}>
          <SoftTypography sx={{ fontSize: "16px", color: c.text, fontWeight: 600, whiteSpace: "nowrap" }}>Execution logs</SoftTypography>
          <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ justifyContent: { xs: "flex-start", lg: "flex-end" } }}>

            <ToolbarDropdown isDark={isDark} c={c} minWidth={140} value={timeRange.value}
              onChange={(v) => { const tr = TIME_RANGES.find((r) => r.value === v); if (tr) setTimeRange(tr); }}
              options={TIME_RANGES.map((r) => ({ label: r.label, value: r.value }))}
            />

            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "12px", color: c.muted, whiteSpace: "nowrap", userSelect: "none" }}>Auto refresh</span>
              <ToolbarDropdown isDark={isDark} c={c} minWidth={110} accent={autoRefreshInterval > 0} value={autoRefreshInterval}
                onChange={(v) => setAutoRefreshInterval(v)}
                options={AUTO_REFRESH_OPTIONS.map((o) => ({ label: o.label, value: o.value, icon: <SyncIcon style={{ fontSize: "14px", color: o.value > 0 ? "var(--primary-color, #7443ff)" : c.muted }} /> }))}
              />
            </div>

            <Tooltip title={autoRefreshInterval > 0 ? `Auto-refreshing · Next in ${nextRefreshIn}s` : "Refresh"}>
              <span>
                <div onClick={refresh} style={{ width: "36px", height: "36px", borderRadius: "8px", flexShrink: 0, border: `1px solid ${autoRefreshInterval > 0 ? "var(--primary-color, #7443ff)" : c.border}`, backgroundColor: c.inputBg, display: "flex", alignItems: "center", justifyContent: "center", cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.6 : 1, position: "relative" }}
                  onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = c.rowHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = c.inputBg; }}>
                  {isLoading ? <CircularProgress size={15} sx={{ color: "var(--primary-color, #7443ff)" }} /> : (
                    <>
                      <RefreshIcon sx={{ fontSize: "18px", color: "var(--primary-color, #7443ff)" }} />
                      {autoRefreshInterval > 0 && nextRefreshIn !== null && (
                        <span style={{ position: "absolute", bottom: "-1px", right: "-1px", fontSize: "8px", fontFamily: "monospace", fontWeight: 700, color: "#fff", backgroundColor: "var(--primary-color, #7443ff)", borderRadius: "4px", padding: "0 3px", lineHeight: "13px", minWidth: "13px", textAlign: "center" }}>{nextRefreshIn}</span>
                      )}
                    </>
                  )}
                </div>
              </span>
            </Tooltip>

            <div style={{ display: "flex", borderRadius: "8px", overflow: "hidden", border: `1px solid ${c.border}`, backgroundColor: c.inputBg }}>
              {["Logs", "Table", "JSON"].map((tab, idx) => (
                <div key={tab} onClick={() => { setViewMode(tab); setSubTab("Logs"); }}
                  style={{ padding: "0 14px", height: "36px", display: "flex", alignItems: "center", fontSize: "13px", cursor: "pointer", color: tab === viewMode ? c.text : c.muted, backgroundColor: tab === viewMode ? (isDark ? c.rowHover : "#e8ebf4") : "transparent", borderRight: idx < 2 ? `1px solid ${c.border}` : "none", userSelect: "none", fontWeight: tab === viewMode ? 600 : 400 }}
                  onMouseEnter={(e) => { if (tab !== viewMode) e.currentTarget.style.backgroundColor = c.rowHover; }}
                  onMouseLeave={(e) => { if (tab !== viewMode) e.currentTarget.style.backgroundColor = "transparent"; }}>
                  {tab}
                </div>
              ))}
            </div>

            <SoftBox display="flex" alignItems="center" gap={0.5}>
              <ToolbarDropdown isDark={isDark} c={c} minWidth={80} value={downloadFormat} onChange={(v) => setDownloadFormat(v)}
                options={[{ label: "JSON", value: "json" }, { label: "CSV", value: "csv" }, { label: "TXT", value: "txt" }]}
              />
              <Tooltip title={`Download as ${downloadFormat.toUpperCase()}`}>
                <span>
                  <div onClick={() => downloadLogs(downloadFormat)} style={{ width: "36px", height: "36px", borderRadius: "8px", border: `1px solid ${c.border}`, backgroundColor: c.inputBg, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.rowHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = c.inputBg; }}>
                    <FileDownloadIcon sx={{ fontSize: "18px", color: "var(--primary-color, #7443ff)" }} />
                  </div>
                </span>
              </Tooltip>
            </SoftBox>

            <Tooltip title="Clear logs">
              <span>
                <div onClick={clearLogs} style={{ width: "36px", height: "36px", borderRadius: "8px", border: `1px solid ${c.border}`, backgroundColor: c.inputBg, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.rowHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = c.inputBg; }}>
                  <DeleteOutlineIcon sx={{ fontSize: "18px", color: c.muted }} />
                </div>
              </span>
            </Tooltip>
          </SoftBox>
        </SoftBox>
      </SoftBox>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <SoftBox display="flex" flex={1} overflow="hidden">

        {/* LEFT SIDEBAR */}
        <SoftBox sx={{ width: "228px", flexShrink: 0, borderRight: `1px solid ${c.border}`, backgroundColor: c.sidebarBg, display: { xs: "none", md: "flex" }, flexDirection: "column", overflowY: "auto" }}>
          <SoftBox px={1.5} pt={1.5} pb={0.75}>
            <TextField placeholder="Search fields by name" fullWidth size="small" value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)}
              sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "12px", backgroundColor: c.inputBg, borderRadius: "8px" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: c.border } }} />
          </SoftBox>

          <SoftBox px={1.5} py={0.75} display="flex" alignItems="center" gap={1} sx={{ cursor: "pointer", borderBottom: `1px solid ${c.border}` }} onClick={() => toggleOpt("showLogLevel")}>
            <div style={{ width: "15px", height: "15px", borderRadius: "4px", flexShrink: 0, backgroundColor: displayOpts.showLogLevel ? "var(--primary-color, #7443ff)" : "transparent", border: `2px solid ${displayOpts.showLogLevel ? "var(--primary-color, #7443ff)" : c.muted}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {displayOpts.showLogLevel && <span style={{ color: "#fff", fontSize: "9px", lineHeight: 1 }}>✓</span>}
            </div>
            <SoftTypography sx={{ fontSize: "12px", color: c.text }}>Show log level</SoftTypography>
          </SoftBox>

          {selectedFields.length > 0 && (
            <SoftBox sx={{ borderBottom: `1px solid ${c.border}` }}>
              <SectionHeader label="SELECTED FIELDS" collapsed={collapsedSections.selectedFields} onToggle={() => toggleSection("selectedFields")} isDark={isDark} c={c} />
              {!collapsedSections.selectedFields && (
                <SoftBox px={1.5} pb={1} display="flex" flexDirection="column" gap={0.25}>
                  <SoftBox display="flex" justifyContent="flex-end">
                    <SoftTypography onClick={() => setSelectedFields([])} sx={{ fontSize: "10px", color: "var(--primary-color, #7443ff)", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>Reset</SoftTypography>
                  </SoftBox>
                  {selectedFields.map((name) => (
                    <SoftBox key={name} display="flex" alignItems="center" gap={0.5} sx={{ py: 0.5 }}>
                      <CheckBoxIcon sx={{ fontSize: "14px", color: "var(--primary-color, #7443ff)", flexShrink: 0 }} />
                      <SoftTypography onClick={() => toggleSelectedField(name)} sx={{ fontSize: "12px", color: "var(--light-primary-color, #7443ff)", flex: 1, fontWeight: 600, cursor: "pointer" }}>{stripDunder(name)}</SoftTypography>
                      <DragIndicatorIcon sx={{ fontSize: "14px", color: c.muted, cursor: "grab" }} />
                    </SoftBox>
                  ))}
                </SoftBox>
              )}
            </SoftBox>
          )}

          <SoftBox sx={{ borderBottom: `1px solid ${c.border}` }}>
            <SectionHeader label="SUGGESTED" collapsed={collapsedSections.suggested} onToggle={() => toggleSection("suggested")} isDark={isDark} c={c} />
            {!collapsedSections.suggested && (
              <SoftBox px={1.5} pb={1} display="flex" flexDirection="column" gap={0.25}>
                {["Log line"].map((f) => (
                  <SoftBox key={f} display="flex" alignItems="center" gap={0.75} sx={{ py: 0.5, cursor: "pointer" }} onClick={() => toggleSelectedField(f)}>
                    {selectedFields.includes(f) ? <CheckBoxIcon sx={{ fontSize: "14px", color: "var(--primary-color, #7443ff)" }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: "14px", color: c.muted }} />}
                    <SoftTypography sx={{ fontSize: "12px", color: c.text }}>{f}</SoftTypography>
                  </SoftBox>
                ))}
              </SoftBox>
            )}
          </SoftBox>

          <SoftBox>
            <SectionHeader label="FIELDS" collapsed={collapsedSections.fields} onToggle={() => toggleSection("fields")} isDark={isDark} c={c} />
            {!collapsedSections.fields && (
              <SoftBox px={1.5} pb={2} display="flex" flexDirection="column" gap={0.5}>
                {filteredFieldStats.length === 0
                  ? <SoftTypography sx={{ color: c.muted, fontStyle: "italic", fontSize: "11px" }}>No fields detected</SoftTypography>
                  : filteredFieldStats.map((field) => {
                    const isSelected   = selectedFields.includes(field.name);
                    const activeFilter = fieldFilters.find((f) => f.key === field.name);
                    return (
                      <SoftBox key={field.name}>
                        <SoftBox display="flex" alignItems="center" gap={0.75} sx={{ px: 0.5, py: 0.5, borderRadius: "4px", cursor: "pointer", "&:hover": { backgroundColor: isDark ? c.rowHover : "#f0f4ff" } }}>
                          <span onClick={(e) => { e.stopPropagation(); toggleSelectedField(field.name); }} style={{ display: "flex", alignItems: "center", flexShrink: 0, cursor: "pointer" }}>
                            {isSelected ? <CheckBoxIcon sx={{ fontSize: "14px", color: "var(--primary-color, #7443ff)" }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: "14px", color: c.muted }} />}
                          </span>
                          <span style={{ flex: 1, minWidth: 0 }} onClick={() => (activeFilter?.value === "*" ? removeFieldFilter(field.name, "*") : addFieldFilter(field.name, "*"))}>
                            <SoftTypography sx={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: activeFilter ? "#4ade80" : isSelected ? "var(--light-primary-color, #7443ff)" : c.text, fontWeight: activeFilter || isSelected ? 600 : 400 }}>{stripDunder(field.name)}</SoftTypography>
                          </span>
                          <SoftTypography sx={{ fontSize: "11px", color: c.muted, flexShrink: 0 }}>{field.percentage}%</SoftTypography>
                        </SoftBox>
                        <FieldBar pct={field.percentage} color={activeFilter ? "#4ade80" : isSelected ? "var(--primary-color, #7443ff)" : "#17c1e8"} />
                      </SoftBox>
                    );
                  })
                }
              </SoftBox>
            )}
          </SoftBox>
        </SoftBox>

        {/* LOG CONTENT */}
        <SoftBox flex={1} display="flex" flexDirection="column" overflow="hidden" minWidth={0}>

          {/* Sub-tab bar */}
          <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${c.border}`, backgroundColor: c.header, flexShrink: 0 }}>
            {subTabs.map((tab) => {
              const isActive = subTab === tab.key;
              return (
                <div key={tab.key} onClick={() => setSubTab(tab.key)}
                  style={{ display: "flex", alignItems: "center", gap: "5px", padding: "8px 14px", cursor: "pointer", fontSize: "13px", fontWeight: isActive ? 600 : 400, color: isActive ? c.text : c.muted, borderBottom: isActive ? "2px solid var(--primary-color, #7443ff)" : "2px solid transparent", userSelect: "none" }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = isDark ? c.text : "#374151"; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = c.muted; }}>
                  {tab.label}
                  <span style={{ fontSize: "11px", padding: "1px 5px", borderRadius: "8px", backgroundColor: isActive ? "var(--primary-color, #7443ff)" : c.border, color: isActive ? "#fff" : c.muted, fontWeight: 600 }}>{tab.count}</span>
                </div>
              );
            })}
          </div>

          {/* Log volume histogram */}
          {subTab === "Logs" && viewMode === "Logs" && (
            <LogVolumeChart logs={logs} timeRange={timeRange} isDark={isDark} c={c} levelFilters={levelFilters} toggleLevelFilter={toggleLevelFilter} onClearLevelFilters={() => setLevelFilters(new Set())} />
          )}

          {/* Filter / status bar — NO pagination */}
          {subTab === "Logs" && (
            <SoftBox sx={{ borderBottom: `1px solid ${c.border}`, flexShrink: 0, backgroundColor: c.bg, display: "flex", flexDirection: "column", px: 1.5, pt: 1, pb: 0.75, gap: 0.75 }}>
              {displayOpts.showSearchBar && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", height: "36px", borderRadius: "8px", border: `1px solid ${c.border}`, backgroundColor: c.inputBg, padding: "0 10px" }}>
                  <SearchIcon style={{ fontSize: "16px", color: c.muted, flexShrink: 0 }} />
                  <input value={filterText} onChange={(e) => setFilterText(e.target.value)} placeholder="Filter logs..." style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: "13px", color: c.text, fontFamily: "sans-serif" }} />
                  {filterText && (
                    <span onClick={() => setFilterText("")} style={{ cursor: "pointer", display: "flex", alignItems: "center", color: c.muted }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = isDark ? "#f87171" : "#dc2626"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = c.muted; }}>
                      <DeleteOutlineIcon style={{ fontSize: "15px" }} />
                    </span>
                  )}
                </div>
              )}

              {fieldFilters.length > 0 && (
                <SoftBox display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                  {fieldFilters.map((f) => (
                    <Chip key={`${f.key}=${f.value}`} label={f.value === "*" ? `has:${stripDunder(f.key)}` : `${stripDunder(f.key)}=${f.value}`} size="small" onDelete={() => removeFieldFilter(f.key, f.value)} sx={{ backgroundColor: isDark ? "#2d2344" : "#eee8ff", color: "var(--light-primary-color, #7443ff)", fontSize: "10px", height: "22px", fontFamily: "monospace" }} />
                  ))}
                  <SoftTypography onClick={() => setFieldFilters([])} sx={{ fontSize: "11px", color: "#f87171", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>Clear all</SoftTypography>
                </SoftBox>
              )}

              {/* Status bar — count only, no pagination */}
              <SoftBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <SoftTypography sx={{ fontSize: "11px", color: c.muted }}>
                  {filteredLogs.length.toLocaleString()} logs · {total.toLocaleString()} total · {timeRange.label}
                </SoftTypography>
                {lastFetch && (
                  <SoftTypography sx={{ fontSize: "11px", color: c.muted }}>
                    Updated {lastFetch.toLocaleTimeString()}
                  </SoftTypography>
                )}
                {autoRefreshInterval > 0 && (
                  <SoftTypography sx={{ fontSize: "11px", color: "var(--primary-color, #7443ff)", fontWeight: 600 }}>
                    ↻ {AUTO_REFRESH_OPTIONS.find((o) => o.value === autoRefreshInterval)?.label}
                    {nextRefreshIn !== null && ` · ${nextRefreshIn}s`}
                  </SoftTypography>
                )}
              </SoftBox>
            </SoftBox>
          )}

          {/* ── Virtual scrolled log list — no pagination at all ───────────── */}
          {subTab === "Logs" && viewMode === "Logs" && (
            <SoftBox
              ref={logContainerRef}
              sx={{ flex: 1, overflowY: "auto", overflowX: "auto", py: 0.5, backgroundImage: isDark ? "repeating-linear-gradient(transparent, transparent 19px, rgba(255,255,255,0.015) 20px)" : "none" }}
            >
              {isLoading && !logs.length && (
                <SoftBox display="flex" justifyContent="center" pt={6}><CircularProgress size={24} /></SoftBox>
              )}
              {!isLoading && !filteredLogs.length && (
                <SoftBox sx={{ minHeight: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, px: 3, textAlign: "center" }}>
                  <SearchIcon sx={{ fontSize: "32px", color: c.muted, opacity: 0.7 }} />
                  <SoftTypography sx={{ color: c.text, fontSize: "15px", fontWeight: 600 }}>
                    {logs.length ? "No logs match these filters" : "No logs in this time range"}
                  </SoftTypography>
                  <SoftTypography sx={{ color: c.muted, fontSize: "13px" }}>
                    {logs.length ? "Adjust the filters to see more results." : "Choose a longer time range to look further back."}
                  </SoftTypography>
                </SoftBox>
              )}
              {filteredLogs.length > 0 && (
                <div style={{ padding: "0 8px", minWidth: "100%", display: "inline-block", boxSizing: "border-box" }}>
                  <VirtualLogList
                    logs={filteredLogs}
                    expandedRows={expandedRows}
                    onToggle={toggleRow}
                    onFieldClick={addFieldFilter}
                    selectedFields={selectedFields}
                    displayMs={displayOpts.displayMs}
                    wrapLines={displayOpts.wrapLines}
                    fontSizeOption={displayOpts.fontSize}
                    highlightText={displayOpts.highlightText}
                    isDark={isDark}
                    c={c}
                    scrollRef={logContainerRef}
                  />
                </div>
              )}
            </SoftBox>
          )}

          {subTab === "Logs"    && viewMode === "Table" && (
            <div style={{ flex: 1, overflow: "auto" }}>
              <table style={{ width: "max-content", minWidth: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["time", "level", "flow_id", "flow_name", "line"].map((col) => (
                      <th key={col} style={{ padding: "4px 8px", textAlign: "left", fontSize: "11px", fontWeight: 600, color: c.muted, backgroundColor: c.header, borderBottom: `1px solid ${c.border}`, borderRight: `1px solid ${c.border}`, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1, fontFamily: "sans-serif", letterSpacing: "0.05em" }}>
                        {stripDunder(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log, i) => {
                    const levelColor = LEVEL_COLORS[log.level] || c.muted;
                    return (
                      <tr key={log.id || i} style={{ cursor: "pointer", borderLeft: `2px solid ${levelColor}` }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.rowHover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}>
                        <td style={{ padding: "2px 8px", fontSize: "11px", color: c.muted, borderBottom: `1px solid ${c.border}`, fontFamily: "monospace", whiteSpace: "nowrap" }}>{log.time}</td>
                        <td style={{ padding: "2px 8px", fontSize: "11px", color: levelColor, borderBottom: `1px solid ${c.border}`, fontFamily: "monospace", fontWeight: 700, textTransform: "uppercase" }}>{log.level}</td>
                        <td style={{ padding: "2px 8px", fontSize: "11px", color: c.text, borderBottom: `1px solid ${c.border}`, fontFamily: "monospace", whiteSpace: "nowrap", maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis" }}>{log.fields?.flow_id ?? log.labels?.flow_id ?? "—"}</td>
                        <td style={{ padding: "2px 8px", fontSize: "11px", color: c.text, borderBottom: `1px solid ${c.border}`, fontFamily: "monospace", whiteSpace: "nowrap", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis" }}>{log.fields?.flow_name ?? log.labels?.flow_name ?? "—"}</td>
                        <td style={{ padding: "2px 8px", fontSize: "11px", color: c.text, borderBottom: `1px solid ${c.border}`, fontFamily: "monospace", whiteSpace: "nowrap", maxWidth: "400px", overflow: "hidden", textOverflow: "ellipsis" }}>{log.line}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {subTab === "Logs"     && viewMode === "JSON"     && (
            <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
              <div style={{ width: "260px", flexShrink: 0, borderRight: `1px solid ${c.border}`, overflowY: "auto" }}>
                {filteredLogs.map((log, i) => {
                  const levelColor = LEVEL_COLORS[log.level] || c.muted;
                  return (
                    <div key={log.id || i} style={{ padding: "6px 10px", cursor: "pointer", borderBottom: `1px solid ${c.border}` }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.rowHover; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}>
                      <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "2px" }}>
                        <span style={{ fontSize: "10px", color: levelColor, fontWeight: 700, textTransform: "uppercase" }}>{log.level || "INFO"}</span>
                        <span style={{ fontSize: "10px", color: c.muted, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.time}</span>
                      </div>
                      <div style={{ fontSize: "11px", color: c.text, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.line}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
                <div style={{ color: c.muted, fontSize: "12px", textAlign: "center", marginTop: "24px" }}>← Select a log entry to view JSON</div>
              </div>
            </div>
          )}

          {subTab === "Labels"   && <LabelsTab   logs={logs} onFilterClick={addFieldFilter} isDark={isDark} c={c} />}
          {subTab === "Fields"   && <FieldsTab   logs={logs} onFilterClick={addFieldFilter} isDark={isDark} c={c} />}
          {subTab === "Patterns" && <PatternsTab logs={logs} isDark={isDark} c={c} />}
        </SoftBox>

        {/* RIGHT PANEL */}
        <SoftBox sx={{ width: "200px", flexShrink: 0, borderLeft: `1px solid ${c.border}`, backgroundColor: c.panelBg, display: { xs: "none", lg: "flex" }, flexDirection: "column", overflowY: "auto" }}>

          <PanelGroupHeader label="DISPLAY" collapsed={collapsedPanelSections.display} onToggle={() => togglePanelSection("display")} isDark={isDark} c={c} />
          {!collapsedPanelSections.display && (
            <>
              <PanelToggle label="Newest logs first" active={displayOpts.newestFirst}   onClick={() => toggleOpt("newestFirst")}   isDark={isDark} c={c} />
              <PanelToggle label="Display ms"        active={displayOpts.displayMs}      onClick={() => toggleOpt("displayMs")}      isDark={isDark} c={c} />
              <PanelToggle label="Wrap disabled"     active={!displayOpts.wrapLines}     onClick={() => toggleOpt("wrapLines")}     isDark={isDark} c={c} icon={<WrapTextIcon style={{ fontSize: "15px" }} />} />
              <PanelToggle label="Highlight text"    active={displayOpts.highlightText}  onClick={() => toggleOpt("highlightText")} isDark={isDark} c={c} />
              <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px", borderBottom: `1px solid ${c.border}` }}>
                <FormatSizeIcon style={{ fontSize: "15px", color: c.muted, flexShrink: 0 }} />
                <span style={{ fontSize: "12px", color: c.text, fontFamily: "sans-serif", flex: 1 }}>Font size</span>
                <div style={{ display: "flex", gap: "3px" }}>
                  {["small", "medium", "large"].map((size) => {
                    const isActive = displayOpts.fontSize === size;
                    return (
                      <button key={size} onClick={() => setDisplayOpts((p) => ({ ...p, fontSize: size }))}
                        style={{ padding: "2px 7px", fontSize: "10px", fontWeight: isActive ? 700 : 400, cursor: "pointer", border: `1px solid ${isActive ? "var(--primary-color, #7443ff)" : c.border}`, borderRadius: "4px", backgroundColor: isActive ? "var(--primary-color, #7443ff)" : "transparent", color: isActive ? "#fff" : c.muted, fontFamily: "sans-serif" }}>
                        {size[0].toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <PanelGroupHeader label="FILTER" collapsed={collapsedPanelSections.filter} onToggle={() => togglePanelSection("filter")} isDark={isDark} c={c} />
          {!collapsedPanelSections.filter && (
            <>
              <PanelToggle label="Search logs"   active={displayOpts.showSearchBar} onClick={() => toggleOpt("showSearchBar")} isDark={isDark} c={c} icon={<SearchIcon     style={{ fontSize: "15px" }} />} />
              <PanelToggle label="Deduplication" active={displayOpts.deduplication} onClick={() => toggleOpt("deduplication")} isDark={isDark} c={c} icon={<FilterListIcon style={{ fontSize: "15px" }} />} />

              <div onClick={() => setShowLevelFilter((p) => !p)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 16px", borderBottom: `1px solid ${c.border}`, cursor: "pointer" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.rowHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}>
                <span style={{ fontSize: "13px", color: c.text, fontFamily: "sans-serif" }}>Filter levels</span>
                <KeyboardArrowDownIcon style={{ fontSize: "14px", color: c.muted, transform: showLevelFilter ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.15s" }} />
              </div>

              {showLevelFilter && (
                <div style={{ padding: "8px 12px", borderBottom: `1px solid ${c.border}`, display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {detectedLevels.size === 0
                    ? <span style={{ fontSize: "11px", color: c.muted, fontStyle: "italic" }}>No logs loaded</span>
                    : (
                      <>
                        {[...detectedLevels].map((level) => {
                          const color    = LEVEL_COLORS[level] || "#8b8fa8";
                          const isActive = levelFilters.has(level);
                          return (
                            <div key={level} onClick={() => toggleLevelFilter(level)}
                              style={{ display: "flex", alignItems: "center", gap: "5px", padding: "3px 10px", borderRadius: "12px", cursor: "pointer", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", userSelect: "none", border: `1px solid ${color}`, backgroundColor: isActive ? color : "transparent", color: isActive ? "#fff" : color, transition: "all 0.15s ease" }}
                              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = `${color}22`; }}
                              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = "transparent"; }}>
                              {level}
                            </div>
                          );
                        })}
                        {levelFilters.size > 0 && (
                          <div onClick={() => setLevelFilters(new Set())} style={{ padding: "3px 10px", borderRadius: "12px", cursor: "pointer", fontSize: "11px", color: "#f87171", border: "1px solid #f87171", userSelect: "none" }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(248,113,113,0.1)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}>
                            clear
                          </div>
                        )}
                      </>
                    )
                  }
                </div>
              )}
            </>
          )}

          <PanelGroupHeader label="ACTIONS" collapsed={collapsedPanelSections.actions} onToggle={() => togglePanelSection("actions")} isDark={isDark} c={c} />
          {!collapsedPanelSections.actions && (
            <>
              <PanelToggle label="Expanded" isDark={isDark} c={c} active={expandedRows.size > 0}
                onClick={() => {
                  if (expandedRows.size > 0) { setExpandedRows(new Set()); }
                  else {
                    const ids = new Set(filteredLogs.map((l, i) => l.id || String(i)));
                    (typeof requestIdleCallback !== "undefined" ? requestIdleCallback : setTimeout)(() => setExpandedRows(ids));
                  }
                }}
                icon={<KeyboardArrowDownIcon style={{ fontSize: "15px" }} />}
              />
              <PanelToggle label="Scroll to bottom" onClick={scrollToBottom} isDark={isDark} c={c} icon={<KeyboardArrowDownIcon style={{ fontSize: "15px" }} />} />
              <PanelToggle label="Scroll to top"    onClick={scrollToTop}    isDark={isDark} c={c} icon={<KeyboardArrowUpIcon   style={{ fontSize: "15px" }} />} />
              <PanelToggle label="Download logs"    onClick={() => downloadLogs(downloadFormat)} isDark={isDark} c={c} icon={<FileDownloadIcon style={{ fontSize: "15px" }} />} />
            </>
          )}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}
